import { memoize } from "lodash";
import { nameToApiIdentifier } from "../../ApiIdentified";
import { assert } from "../../utils";
import type { ShopifyApiVersion } from "./ShopifyApiVersion";
import { isModelSpecDisconnected, ShopifyModelKeys, shopifyModelSpecForVersion } from "./ShopifyModelMetadata";

export const maybeGetShopifyModelApiIdentifierByModelKey = memoize(
  (modelKey: string, apiVersion: ShopifyApiVersion, frameworkVersionConstraint: string) => {
    const spec = shopifyModelSpecForVersion(modelKey, apiVersion, frameworkVersionConstraint);
    if (spec) {
      return nameToApiIdentifier(spec.name);
    }
  },
  (modelKey, apiVersion) => `${modelKey}--${apiVersion}`
);

export const getShopifyModelApiIdentifierByModelKey = (
  modelKey: string,
  apiVersion: ShopifyApiVersion,
  frameworkVersionConstraint: string
) => {
  return assert(
    maybeGetShopifyModelApiIdentifierByModelKey(modelKey, apiVersion, frameworkVersionConstraint),
    `Could not get the Shopify model API identifier for model key "${modelKey}"`
  );
};

export const maybeGetShopifyModelKeyByDefaultApiIdentifier = memoize(
  (defaultApiIdentifier: string, apiVersion: ShopifyApiVersion, frameworkVersionConstraint: string) => {
    const modelKey = ShopifyModelKeys.find((modelKey) => {
      const spec = shopifyModelSpecForVersion(modelKey, apiVersion, frameworkVersionConstraint);
      if (spec && !isModelSpecDisconnected(spec, frameworkVersionConstraint)) {
        return nameToApiIdentifier(spec.name) === defaultApiIdentifier;
      }
    });

    return modelKey;
  },
  (modelKey, apiVersion) => `${modelKey}--${apiVersion}`
);

export const getShopifyModelKeyByDefaultApiIdentifier = (
  defaultApiIdentifier: string,
  apiVersion: ShopifyApiVersion,
  frameworkVersionConstraint: string
) => {
  return assert(
    maybeGetShopifyModelKeyByDefaultApiIdentifier(defaultApiIdentifier, apiVersion, frameworkVersionConstraint),
    `Could not get the Shopify model key for API identifier "${defaultApiIdentifier}"`
  );
};
